import { graphql } from 'react-relay';
import { useContext, useEffect, useMemo } from 'react';
import { addMonths, isFuture } from 'date-fns';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { SPAViewContext } from '@confluence/spa-view-context/entry-points/SPAViewContext.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
// eslint-disable-next-line confluence-targeting-usage/no-legacy-tap-personalization-api -- To be migrated to useSiteTraits. See ACTIVATION-2527
import { useSitePersonalizationQuery } from '@confluence/onboarding-helpers/entry-points/hooks/useSitePersonalizationQuery.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';

import type { useMigratorPromptEligibleAABMigratorPromptQuery as useMigratorPromptEligibleAABMigratorPromptQuery$data } from './__types__/useMigratorPromptEligibleAABMigratorPromptQuery.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
export const useMigratorPromptEligible = ({
	skip = false,
}: {
	skip?: boolean | undefined;
} = {}): {
	isMigratorPromptEligible: boolean;
	loading: boolean;
} => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	// Feature and role checks
	const { isSiteAdmin } = useContext(SPAViewContext);
	const shouldSkip = skip || !isSiteAdmin;

	// Session data
	const { cloudId } = useSessionData();

	// Query for AABTableData
	const {
		data: AABTableData,
		loading: AABTableLoading,
		error: AABTableError,
	} = useQueryInterop<useMigratorPromptEligibleAABMigratorPromptQuery$data>(
		graphql`
			query useMigratorPromptEligibleAABMigratorPromptQuery {
				adminAnnouncementBannerSettingsByCriteria(first: 1, orderBy: DEFAULT, isAscending: false) {
					nodes {
						id
					}
				}
			}
		`,
		{
			skip: shouldSkip,
		},
	);

	// Query for site personalization data
	const {
		attributes,
		loading: sitePersonalizationLoading,
		error: sitePersonalizationError,
	} = useSitePersonalizationQuery({
		cloudId,
		skip: shouldSkip,
	});

	// Processing site personalization data
	const sitePersonalizationDataAttributes = attributes?.reduce(
		(obj, item) => ({ ...obj, [item.name]: item.value }),
		{},
	);

	// Migration date calculations
	const migrationCompletionDate: string | undefined =
		sitePersonalizationDataAttributes?.['confluence_site_migration_completion_date'];

	const dateSixMonthsAfterMigration = migrationCompletionDate
		? addMonths(new Date(migrationCompletionDate), 6)
		: undefined;

	const isWithinSixMonthsAfterMigration = dateSixMonthsAfterMigration
		? isFuture(dateSixMonthsAfterMigration)
		: false;

	// Final loading and error states
	const loading = AABTableLoading || sitePersonalizationLoading;

	const error = useMemo(
		() => AABTableError || sitePersonalizationError,
		[AABTableError, sitePersonalizationError],
	);

	useEffect(() => {
		// Error handling
		if (error) {
			getMonitoringClient().submitError(error, {
				attribution: Attribution.ADMIN_EXPERIENCE,
			});
			markErrorAsHandled(error);
		}
	}, [createAnalyticsEvent, error]);

	// Check for existing admin announcement banners
	const hasAdminAnnouncementBanners =
		!!AABTableData?.adminAnnouncementBannerSettingsByCriteria?.nodes?.length;

	// Eligibility check
	const isMigratorPromptEligible =
		!shouldSkip &&
		!loading &&
		!error &&
		isWithinSixMonthsAfterMigration &&
		!hasAdminAnnouncementBanners;

	return {
		isMigratorPromptEligible,
		loading,
	};
};
